/* UC floating hero. Outfit loop (a colorway fit between every White fit), the Boxy Blank orbit (front of him or behind
   by depth, turning to show the back on the far side), film grain. window.UCFloat.mount(stage, data, opts) builds it
   inside any box (the homepage hero, the signup popup) and returns { pause, step, destroy }.
   Homepage: the headline + caption follow the shirt in front, arrows turn the orbit one shirt, pause stops both.
   Reduced motion: still frame, orbit parked. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const el = (tag, cls, parent, attr) => { const e = document.createElement(tag); if (cls) e.className = cls; if (attr) e.setAttribute(attr, ''); parent.appendChild(e); return e; };

  function mount(stage, data, o = {}) {
    if (!stage || !data || !data.fits || !data.fits.length) return null;
    let alive = true, paused = reduce;
    const q = s => stage.querySelector(s);
    const behind = q('[data-uc-float-behind]') || el('div', 'uc-float__orbit', stage, 'data-uc-float-behind');
    if (!q('.uc-float__halo')) el('div', 'uc-float__halo', stage);
    if (!q('.uc-float__floor')) el('div', 'uc-float__floor', stage);
    const figBox = q('.uc-float__fig') || el('div', 'uc-float__fig', stage);
    let fitImg = q('[data-uc-float-fit]');
    if (!fitImg) { fitImg = el('img', '', figBox, 'data-uc-float-fit'); fitImg.alt = ''; fitImg.draggable = false; fitImg.src = data.fits[0].src; }
    const front = q('[data-uc-float-front]') || el('div', 'uc-float__orbit uc-float__orbit--front', stage, 'data-uc-float-front');
    let cv = q('[data-uc-float-grain]');
    if (!cv) { cv = el('canvas', 'uc-float__grain', stage, 'data-uc-float-grain'); cv.width = 270; cv.height = 200; cv.setAttribute('aria-hidden', 'true'); }

    // ---- outfit loop ----
    const whites = data.fits.filter(f => !f.color), cols = data.fits.filter(f => f.color);
    const seq = cols.length && whites.length ? whites.flatMap((w, i) => [w, cols[i % cols.length]]) : data.fits;
    const imgs = seq.map(f => { const i = new Image(); i.decoding = 'async'; i.src = f.src; return i; });
    const ms = Math.max(120, +o.speed || 250);
    let k = 0, timer = null;
    const tick = () => {
      if (!alive) return;
      if (!paused && !document.hidden && imgs.length) {
        k = (k + 1) % imgs.length;
        const im = imgs[k];
        if (im.complete && im.naturalWidth) fitImg.src = im.src;
      }
      timer = setTimeout(tick, ms);
    };
    timer = setTimeout(tick, ms);

    // ---- orbit ----
    const colors = data.colors || [];
    const reps = colors.length && colors.length <= 4 ? 2 : 1;
    const shirts = [];
    for (let r = 0; r < reps; r++) colors.forEach(c => shirts.push(c));
    const n = shirts.length;
    const els = shirts.map((c, i) => {
      const d = el('div', 'uc-float__shirt', behind);
      const im = el('img', '', d); im.alt = ''; im.draggable = false; im.src = c.front;
      const pre = new Image(); pre.src = c.back;
      return { d, im, c, face: 'front', phase: (i / n) * Math.PI * 2 };
    });
    const period = Math.max(2, +o.hold || 3) * 1000 * Math.max(1, n);
    let spin = 0, target = null, last = performance.now(), frontName = null, raf = 0;
    const frame = now => {
      if (!alive) return;
      const dt = Math.min(64, now - last); last = now;
      if (target !== null) {
        const diff = target - spin; spin += diff * Math.min(1, dt / 160);
        if (Math.abs(diff) < 0.002) { spin = target; target = null; }
      } else if (!paused) spin += (dt / period) * Math.PI * 2;
      const W = stage.clientWidth, H = stage.clientHeight;
      if (W && H) {
        const narrow = W < H;
        const cx = W / 2, cy = H * (narrow ? 0.565 : 0.58), rx = narrow ? W * 0.36 : Math.min(W * 0.36, H * 0.7), ry = H * (narrow ? 0.13 : 0.12);
        let best = null, bz = -2;
        for (const s of els) {
          const a = s.phase - spin, x = Math.sin(a), z = Math.cos(a), depth = (z + 1) / 2;
          const sc = 0.66 + 0.36 * depth, w = s.d.offsetWidth, h = s.d.offsetHeight;
          const face = z >= 0 ? 'front' : 'back';
          if (face !== s.face) { s.face = face; s.im.src = s.c[face] || s.c.front; }
          const turn = face === 'front' ? -x * 62 : x * 62;
          s.d.style.transform = `translate(${cx + x * rx - w / 2}px, ${cy + z * ry - h / 2}px) scale(${sc}) rotateY(${turn}deg)`;
          s.d.style.opacity = (0.55 + 0.45 * depth).toFixed(3);
          s.d.style.filter = depth < 0.45 ? `blur(${((0.45 - depth) * 3.2).toFixed(2)}px)` : 'none';
          const host = z >= 0 ? front : behind;
          if (s.d.parentNode !== host) host.appendChild(s.d);
          s.d.style.zIndex = Math.round(depth * 100);
          if (z > bz) { bz = z; best = s.c; }
        }
        if (best && best.name !== frontName) { frontName = best.name; o.onFront && o.onFront(best); }
      }
      raf = requestAnimationFrame(frame);
    };
    if (n) raf = requestAnimationFrame(frame);

    // ---- grain: one fine 1:1 noise tile, repeated, jumped to a new offset each tick (cheap, never blotchy) ----
    const tile = document.createElement('canvas'); tile.width = tile.height = 160;
    const tctx = tile.getContext('2d'), id = tctx.createImageData(160, 160);
    for (let i = 0; i < id.data.length; i += 4) { const v = 168 + Math.random() * 87; id.data[i] = id.data[i + 1] = id.data[i + 2] = v; id.data[i + 3] = 255; }
    tctx.putImageData(id, 0, 0);
    cv.width = cv.height = 1;
    cv.style.backgroundImage = `url(${tile.toDataURL()})`; cv.style.backgroundSize = '160px 160px';
    const grain = () => { cv.style.backgroundPosition = `${(Math.random() * 160) | 0}px ${(Math.random() * 160) | 0}px`; };
    grain();
    const gt = reduce ? 0 : setInterval(() => { if (!paused && !document.hidden) grain(); }, 83);

    return {
      pause(p) { paused = p === undefined ? !paused : !!p; return paused; },
      step(dir) { if (!n) return; const base = target === null ? spin : target; const unit = (Math.PI * 2) / n; target = Math.round(base / unit) * unit + dir * unit; },
      destroy() { alive = false; clearTimeout(timer); cancelAnimationFrame(raf); clearInterval(gt); },
    };
  }
  window.UCFloat = { mount };

  // ---- homepage hero section ----
  const init = sec => {
    if (sec.dataset.ucFloatReady) return;
    sec.dataset.ucFloatReady = '1';
    let data;
    try { data = JSON.parse(sec.querySelector('[data-uc-float-data]').textContent); } catch (e) { return; }
    const colorEl = sec.querySelector('[data-uc-float-color]'), capEl = sec.querySelector('[data-uc-float-cap]');
    const shopEl = sec.querySelector('[data-uc-float-shop]'), pauseBtn = sec.querySelector('[data-uc-float-pause]');
    const suffix = sec.dataset.suffix || '';
    const ctl = mount(sec.querySelector('[data-uc-float-stage]'), data, {
      speed: sec.dataset.speed, hold: sec.dataset.hold,
      onFront: c => {
        if (colorEl) colorEl.textContent = c.name;
        if (capEl) capEl.textContent = `${c.name} ${suffix}`.trim();
        if (shopEl && c.url) shopEl.href = c.url;
      },
    });
    if (!ctl) return;
    sec.querySelector('[data-uc-float-prev]')?.addEventListener('click', () => ctl.step(-1));
    sec.querySelector('[data-uc-float-next]')?.addEventListener('click', () => ctl.step(1));
    pauseBtn?.addEventListener('click', () => {
      const p = ctl.pause(); sec.classList.toggle('is-paused', p);
      pauseBtn.setAttribute('aria-pressed', String(p)); pauseBtn.setAttribute('aria-label', p ? 'Play' : 'Pause');
    });
    if (reduce) sec.classList.add('is-paused');
  };
  const run = () => document.querySelectorAll('[data-uc-float]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-uc-float]').forEach(init));
})();
